import React, { useState, useMemo, useEffect } from 'react'
import {
  Box, Typography, Button, Paper, Chip, IconButton, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, Alert,
  CircularProgress, InputAdornment, Tooltip, Divider, List,
  ListItem, ListItemText, ListItemSecondaryAction, InputBase,
  MenuItem, Switch
} from '@mui/material'
import { ROUNDING_OPTIONS, roundComponentForMenu, describeRule } from '../utils/menuMarkup'
import AddIcon from '@mui/icons-material/AddRounded'
import EditIcon from '@mui/icons-material/EditOutlined'
import VisibilityIcon from '@mui/icons-material/VisibilityOutlined'
import DeleteIcon from '@mui/icons-material/DeleteOutline'
import SearchIcon from '@mui/icons-material/SearchRounded'
import AccessTimeIcon from '@mui/icons-material/AccessTimeRounded'
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutlineRounded'
import PlaylistAddIcon from '@mui/icons-material/PlaylistAddRounded'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesomeOutlined'
import MenuBookIcon from '@mui/icons-material/MenuBookOutlined'
import CategoryIcon from '@mui/icons-material/CategoryOutlined'
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenuOutlined'
import CloseIcon from '@mui/icons-material/CloseRounded'
import RoomServiceRoundedIcon from '@mui/icons-material/RoomServiceRounded'
import CateringBadge from '../components/Common/CateringBadge'
import { CATERING } from '../utils/catering'
import { useQuery, useMutation } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import { GET_ADVANCED_MENUS, GET_RESTAURANT_ITEMS, GET_ADVANCED_MENU_ITEMS } from '../apollo/advancedMenuQueries'
import {
  CREATE_ADVANCED_MENU, UPDATE_ADVANCED_MENU, DELETE_ADVANCED_MENU,
  ADD_ITEM_TO_MENU, REMOVE_ITEM_FROM_MENU
} from '../apollo/advancedMenuMutations'
import AIFileUpload from '../components/AdvancedMenu/AIFileUpload'
import MenuOverridesEditor from '../components/AdvancedMenu/MenuOverridesEditor'
import { mw, numSx, displaySx, chipSx, iconBtnSx, iconBtnDangerSx, pillBtnSx, pillBtnPrimarySx, aiBtnSx, dialogPaperSx, countPillSx } from './menuWorkspaceStyles'

// The menus table is a CSS grid; header and rows share these columns.
const MENU_GRID = '2fr 1.6fr 2fr 1fr 124px'

// Per-day menu hours (backend migration 088). The stored shape is
// { MON: { open, windows: [{ start, end }] }, ... } — a day can hold more than
// one window for a split shift (9-11 AM and 4-8 PM). The form keeps every day
// present so the switches always have something to toggle.
const MENU_DAYS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN']
const DAY_LABEL = {
  MON: 'Monday', TUE: 'Tuesday', WED: 'Wednesday', THU: 'Thursday',
  FRI: 'Friday', SAT: 'Saturday', SUN: 'Sunday',
}
const DAY_SHORT = { MON: 'Mon', TUE: 'Tue', WED: 'Wed', THU: 'Thu', FRI: 'Fri', SAT: 'Sat', SUN: 'Sun' }

const MAX_WINDOWS_PER_DAY = 4

const emptyDaySchedule = (start = '', end = '') =>
  MENU_DAYS.reduce((acc, d) => ({ ...acc, [d]: { open: true, windows: [{ start, end }] } }), {})

// Stored JSON (or a stringified copy of it) → the form shape. Returns null when
// the menu has no per-day schedule, which is what puts the form back in
// "same hours every day" mode. Reads both the multi-window shape and the single
// { start, end } pair menus were first saved with.
const loadDaySchedule = (raw) => {
  let ds = raw
  if (typeof ds === 'string') {
    try { ds = JSON.parse(ds) } catch (e) { return null }
  }
  if (!ds || typeof ds !== 'object' || Array.isArray(ds) || Object.keys(ds).length === 0) return null
  const hm = (v) => (v ? String(v).slice(0, 5) : '')
  return MENU_DAYS.reduce((acc, d) => {
    const entry = ds[d]
    if (!entry) {
      // A day absent from a saved schedule was closed — keep it that way.
      acc[d] = { open: false, windows: [{ start: '', end: '' }] }
      return acc
    }
    const windows = Array.isArray(entry.windows) && entry.windows.length
      ? entry.windows.map(w => ({ start: hm(w && w.start), end: hm(w && w.end) }))
      : [{ start: hm(entry.start), end: hm(entry.end) }]
    acc[d] = { open: entry.open !== false, windows }
    return acc
  }, {})
}

const buildDaySchedule = (form) =>
  MENU_DAYS.reduce((acc, d) => {
    const day = form[d] || {}
    if (day.open === false) { acc[d] = { open: false }; return acc }
    const windows = (day.windows || [])
      .filter(w => w && (w.start || w.end))
      .map(w => ({ start: w.start || null, end: w.end || null }))
    // No usable window on an open day = serve it all day, which is what a blank
    // pair has always meant.
    acc[d] = { open: true, windows: windows.length ? windows : [{ start: null, end: null }] }
    return acc
  }, {})

// "9:00 AM - 11:00 AM, 4:00 PM - 8:00 PM" for one day's windows.
const formatWindows = (windows, allDayLabel) => {
  const to12h = (hm) => {
    if (!hm) return ''
    const [h, m] = String(hm).split(':').map(Number)
    const period = h >= 12 ? 'PM' : 'AM'
    return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${period}`
  }
  const parts = (windows || [])
    .filter(w => w && (w.start || w.end))
    .map(w => (w.start && w.end ? `${to12h(w.start)} - ${to12h(w.end)}` : w.start ? `From ${to12h(w.start)}` : `Until ${to12h(w.end)}`))
  return parts.length ? parts.join(', ') : allDayLabel
}

const MenusTab = ({ onOpenCreate, onOpenAI, onUploadComplete, onPreview }) => {
  const { t } = useTranslation()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [aiOpen, setAiOpen] = useState(false)
  const [aiIntoMenuOpen, setAiIntoMenuOpen] = useState(false)
  const [editingMenu, setEditingMenu] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [startTime, setStartTime] = useState('')
  const [endTime, setEndTime] = useState('')
  // Hours mode: one window for the whole week (startTime/endTime, how menus have
  // always worked) or a window per day, which is what daySchedule stores.
  const [hoursMode, setHoursMode] = useState('EVERY_DAY')
  const [daySchedule, setDaySchedule] = useState(emptyDaySchedule())
  const [markupPercent, setMarkupPercent] = useState('')
  const [roundingMode, setRoundingMode] = useState('AS_IS')
  const [roundingValue, setRoundingValue] = useState('')
  // Per-category and per-item override maps (mirrors backend JSON columns).
  const [categoryOverrides, setCategoryOverrides] = useState({})
  const [itemOverrides, setItemOverrides] = useState({})
  const [error, setError] = useState('')
  const [itemSearchQuery, setItemSearchQuery] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')

  const restaurantId = localStorage.getItem('restaurantId')

  const { data, loading: queryLoading, refetch: refetchMenusList } = useQuery(GET_ADVANCED_MENUS, {
    variables: { restaurantId }, skip: !restaurantId, fetchPolicy: 'network-only'
  })
  const { data: allItemsData } = useQuery(GET_RESTAURANT_ITEMS, {
    variables: { restaurantId }, skip: !restaurantId
  })
  const { data: menuItemsData, refetch: refetchMenuItems } = useQuery(GET_ADVANCED_MENU_ITEMS, {
    variables: { menuId: editingMenu?._id }, skip: !editingMenu?._id, fetchPolicy: 'network-only'
  })

  const refetchQueries = [{ query: GET_ADVANCED_MENUS, variables: { restaurantId } }]

  const [createMenu, { loading: creating }] = useMutation(CREATE_ADVANCED_MENU, {
    refetchQueries, onCompleted: () => handleCloseDialog(), onError: (err) => setError(err.message)
  })
  const [updateMenu, { loading: updating }] = useMutation(UPDATE_ADVANCED_MENU, {
    refetchQueries, onCompleted: () => handleCloseDialog(), onError: (err) => setError(err.message)
  })
  const [deleteMenu] = useMutation(DELETE_ADVANCED_MENU, {
    refetchQueries, onError: (err) => console.error('Delete error:', err)
  })
  const [addItemToMenu] = useMutation(ADD_ITEM_TO_MENU, {
    onCompleted: () => { if (editingMenu?._id) refetchMenuItems() }, refetchQueries,
    onError: (err) => console.error('Add item error:', err)
  })
  const [removeItemFromMenu] = useMutation(REMOVE_ITEM_FROM_MENU, {
    onCompleted: () => { if (editingMenu?._id) refetchMenuItems() }, refetchQueries,
    onError: (err) => console.error('Remove item error:', err)
  })

  const menus = data?.advancedMenus || []
  const allItems = allItemsData?.advancedRestaurantItems || []
  const menuItems = menuItemsData?.advancedMenuItems || []
  const menuItemIds = useMemo(() => new Set(menuItems.map(i => i._id)), [menuItems])
  const availableItems = useMemo(() => allItems.filter(item => !menuItemIds.has(item._id)), [allItems, menuItemIds])
  const allCategories = useMemo(() => {
    const cats = new Set()
    allItems.forEach(item => {
      if (item.categoryName) item.categoryName.split(',').map(s => s.trim()).filter(Boolean).forEach(c => cats.add(c))
    })
    return [...cats].sort()
  }, [allItems])

  const filteredAvailableItems = useMemo(() => {
    let items = availableItems
    if (itemSearchQuery.trim()) {
      const q = itemSearchQuery.toLowerCase()
      items = items.filter(i => i.name.toLowerCase().includes(q) || (i.categoryName && i.categoryName.toLowerCase().includes(q)))
    }
    if (categoryFilter) items = items.filter(i => i.categoryName && i.categoryName.toLowerCase().includes(categoryFilter.toLowerCase()))
    return items
  }, [availableItems, itemSearchQuery, categoryFilter])

  const filteredMenus = useMemo(() => {
    if (!searchQuery.trim()) return menus
    const q = searchQuery.toLowerCase()
    return menus.filter(m => m.name.toLowerCase().includes(q))
  }, [menus, searchQuery])

  const totalItems = menus.reduce((sum, m) => sum + (m.items?.length || 0), 0)
  const totalCategories = useMemo(() => {
    const cats = new Set()
    menus.forEach(m => (m.items || []).forEach(item => {
      if (item.categoryName) item.categoryName.split(',').map(s => s.trim()).filter(Boolean).forEach(c => cats.add(c))
    }))
    return cats.size
  }, [menus])

  const resetForm = () => {
    setName(''); setDescription(''); setStartTime(''); setEndTime(''); setMarkupPercent('')
    setHoursMode('EVERY_DAY'); setDaySchedule(emptyDaySchedule())
    setRoundingMode('AS_IS'); setRoundingValue('')
    setCategoryOverrides({}); setItemOverrides({})
    setError(''); setEditingMenu(null); setItemSearchQuery(''); setCategoryFilter('')
  }

  // Convert stored override maps (numbers) into the string-valued shape the form
  // inputs use. Tolerant of stringified JSON.
  const loadOverrides = (raw) => {
    let map = raw
    if (typeof map === 'string') { try { map = JSON.parse(map) } catch (e) { map = null } }
    if (!map || typeof map !== 'object') return {}
    const out = {}
    for (const [k, v] of Object.entries(map)) {
      if (!v || typeof v !== 'object') continue
      if (v.type === 'FIXED') {
        out[k] = {
          type: 'FIXED',
          basePrice: v.basePrice != null ? String(v.basePrice) : '',
          variantPrices: Object.fromEntries(Object.entries(v.variantPrices || {}).map(([s, p]) => [s, String(p)])),
        }
      } else if (v.type === 'MARKUP') {
        out[k] = { type: 'MARKUP', pct: v.pct != null ? String(v.pct) : '', mode: v.mode || 'AS_IS', value: v.value ? String(v.value) : '' }
      } else {
        // category rule
        out[k] = { pct: v.pct != null ? String(v.pct) : '', mode: v.mode || 'AS_IS', value: v.value ? String(v.value) : '' }
      }
    }
    return out
  }

  const handleOpenCreate = () => { resetForm(); setDialogOpen(true) }

  // Register handlers with parent refs (no state in parent = no re-renders)
  useEffect(() => {
    if (onOpenCreate) onOpenCreate.current = handleOpenCreate
    if (onOpenAI) onOpenAI.current = () => setAiOpen(true)
  }, [])

  const handleOpenEdit = (menu) => {
    setEditingMenu(menu); setName(menu.name || ''); setDescription(menu.description || '')
    setStartTime(menu.startTime || ''); setEndTime(menu.endTime || '')
    const loadedDays = loadDaySchedule(menu.daySchedule)
    setHoursMode(loadedDays ? 'BY_DAY' : 'EVERY_DAY')
    setDaySchedule(loadedDays || emptyDaySchedule())
    setMarkupPercent(menu.markupPercent ? String(menu.markupPercent) : '')
    setRoundingMode(menu.roundingMode || 'AS_IS')
    setRoundingValue(menu.roundingValue ? String(menu.roundingValue) : '')
    setCategoryOverrides(loadOverrides(menu.categoryOverrides))
    setItemOverrides(loadOverrides(menu.itemOverrides))
    setError(''); setItemSearchQuery(''); setCategoryFilter(''); setDialogOpen(true)
  }
  const handleCloseDialog = () => { setDialogOpen(false); resetForm() }
  const handleSubmit = () => {
    setError('')
    if (!name.trim()) { setError(t('Menu name is required')); return }
    const parsedMarkup = parseFloat(markupPercent)
    if (markupPercent !== '' && (isNaN(parsedMarkup) || parsedMarkup < 0)) {
      setError(t('Markup % must be 0 or a positive number')); return
    }
    // Rounding: AS_IS ignores the value. The ENDING modes accept 0 (a .00 ending
    // = whole-dollar prices), while increment/nearest modes need a positive value
    // (you can't round to a 0 increment) and must be < 1 for an ending.
    const ruleNeedsValue = roundingMode !== 'AS_IS'
    const isEndingMode = roundingMode === 'UP_ENDING' || roundingMode === 'DOWN_ENDING'
    const parsedRoundingValue = parseFloat(roundingValue)
    if (ruleNeedsValue) {
      if (roundingValue === '' || isNaN(parsedRoundingValue)) {
        setError(t('Enter a rounding value')); return
      }
      if (isEndingMode) {
        if (parsedRoundingValue < 0 || parsedRoundingValue > 0.99) {
          setError(t('Price ending must be between 0.00 and 0.99 (e.g. 0.99, 0.95, 0.00)')); return
        }
      } else if (parsedRoundingValue <= 0) {
        setError(t('Increment must be greater than 0 (e.g. 0.05, 0.25, 0.50)')); return
      }
    }
    // Convert the string-valued override maps back to clean numeric objects.
    // The backend sanitizes again, so we just shape + parse here.
    const numOrNull = (s) => { const n = parseFloat(s); return Number.isFinite(n) ? n : null }
    const catOut = {}
    for (const [cat, r] of Object.entries(categoryOverrides)) {
      if (!r) continue
      const pct = numOrNull(r.pct) || 0
      catOut[cat] = { pct, mode: r.mode || 'AS_IS', value: numOrNull(r.value) || 0 }
    }
    const itemOut = {}
    for (const [id, o] of Object.entries(itemOverrides)) {
      if (!o) continue
      if (o.type === 'FIXED') {
        const fixed = { type: 'FIXED' }
        const bp = numOrNull(o.basePrice)
        if (bp != null) fixed.basePrice = bp
        const vp = {}
        for (const [size, p] of Object.entries(o.variantPrices || {})) {
          const n = numOrNull(p)
          if (n != null) vp[size] = n
        }
        if (Object.keys(vp).length) fixed.variantPrices = vp
        if (fixed.basePrice != null || fixed.variantPrices) itemOut[id] = fixed
      } else if (o.type === 'MARKUP') {
        itemOut[id] = { type: 'MARKUP', pct: numOrNull(o.pct) || 0, mode: o.mode || 'AS_IS', value: numOrNull(o.value) || 0 }
      }
    }

    const input = {
      name: name.trim(), description: description.trim(), menuType: 'ALL_DAY',
      startTime: startTime || null, endTime: endTime || null, isActive: true, displayOrder: 0,
      // null restores "same hours every day"; the backend then reads
      // startTime/endTime for every day, exactly as it did before.
      daySchedule: hoursMode === 'BY_DAY' ? buildDaySchedule(daySchedule) : null,
      markupPercent: markupPercent === '' ? 0 : parsedMarkup,
      roundingMode,
      roundingValue: ruleNeedsValue ? parsedRoundingValue : 0,
      categoryOverrides: catOut,
      itemOverrides: itemOut,
    }
    if (editingMenu) updateMenu({ variables: { id: editingMenu._id, input } })
    else createMenu({ variables: { restaurantId, input } })
  }
  const handleDelete = (menuId) => {
    if (window.confirm(t('Are you sure you want to delete this menu?'))) deleteMenu({ variables: { id: menuId } })
  }
  const handleAddItem = (itemId) => { if (editingMenu?._id) addItemToMenu({ variables: { menuId: editingMenu._id, itemId } }) }
  const handleRemoveItem = (itemId) => { if (editingMenu?._id) removeItemFromMenu({ variables: { menuId: editingMenu._id, itemId } }) }
  const handleAddAllFromCategory = (category) => {
    if (!editingMenu?._id) return
    availableItems.filter(i => i.categoryName && i.categoryName.toLowerCase().includes(category.toLowerCase()))
      .forEach(item => addItemToMenu({ variables: { menuId: editingMenu._id, itemId: item._id } }))
  }
  const formatMenuHours = (menu) => {
    const ds = loadDaySchedule(menu.daySchedule)
    if (ds) {
      const openDays = MENU_DAYS.filter(d => ds[d].open)
      if (openDays.length === 0) return t('Closed every day')
      // One shared set of windows across every open day reads better as the
      // windows themselves; anything else is genuinely "varies".
      const signature = (d) => (ds[d].windows || []).map(w => `${w.start}-${w.end}`).join('|')
      const distinct = new Set(openDays.map(signature))
      const label = distinct.size === 1
        ? formatWindows(ds[openDays[0]].windows, t('All Day'))
        : t('Varies by day')
      return openDays.length === MENU_DAYS.length ? label : `${label} · ${openDays.map(d => DAY_SHORT[d]).join(' ')}`
    }
    if (!menu.startTime && !menu.endTime) return t('All Day')
    return [menu.startTime, menu.endTime].filter(Boolean).join(' - ')
  }
  const getCategories = (menu) => {
    const cats = new Set()
    ;(menu.items || []).forEach(item => {
      if (item.categoryName) item.categoryName.split(',').map(s => s.trim()).filter(Boolean).forEach(c => cats.add(c))
    })
    return [...cats].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
  }

  const saving = creating || updating

  return (
    <>
      <Box sx={{ pb: 4 }}>
        {/* Stat Cards */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2.5, mb: 3.5 }}>
          {[
            { label: t('Total Menus'), value: menus.length, icon: <MenuBookIcon sx={{ color: mw.color.ai, fontSize: 24 }} />, bg: mw.color.aiSoft },
            { label: t('Total Categories'), value: totalCategories, icon: <CategoryIcon sx={{ color: mw.color.amber, fontSize: 24 }} />, bg: mw.color.amberSoft },
            { label: t('Total Items'), value: totalItems, icon: <RestaurantMenuIcon sx={{ color: mw.color.emerald, fontSize: 24 }} />, bg: mw.color.emeraldSoft },
          ].map((card, i) => (
            <Paper key={i} elevation={0} sx={{ borderRadius: mw.radius.card, border: `1px solid ${mw.color.border}`, p: 3, display: 'flex', alignItems: 'center', gap: 2.25, bgcolor: mw.color.cardBg, boxShadow: mw.shadow.card }}>
              <Box sx={{ width: 52, height: 52, borderRadius: '14px', bgcolor: card.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                {card.icon}
              </Box>
              <Box>
                <Typography sx={{ ...displaySx(34), fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>
                  {queryLoading ? <CircularProgress size={20} /> : card.value}
                </Typography>
                <Typography sx={{ fontSize: 13.5, fontWeight: 500, color: mw.color.textMuted, mt: 0.75 }}>{card.label}</Typography>
              </Box>
            </Paper>
          ))}
        </Box>

        {/* Table */}
        <Paper elevation={0} sx={{ borderRadius: mw.radius.card, border: `1px solid ${mw.color.border}`, boxShadow: mw.shadow.card, overflow: 'hidden', bgcolor: mw.color.cardBg }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, px: 3, py: 2.5 }}>
            <Typography sx={{ ...displaySx(20), display: 'flex', alignItems: 'center' }}>
              {t('All Menus')}{' '}
              <Typography component="span" sx={{ ...countPillSx(false), height: 24, minWidth: 26, fontSize: 12.5, ml: 1.25, color: mw.color.textSecondary }}>{filteredMenus.length}</Typography>
            </Typography>
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 1, height: 44, px: 1.5,
              width: { xs: '100%', sm: 380 },
              bgcolor: mw.color.cardBg, border: `1px solid ${mw.color.border}`, borderRadius: mw.radius.sm,
              transition: 'border-color .15s ease, box-shadow .15s ease',
              '&:hover': { borderColor: mw.color.borderStrong },
              '&:focus-within': { borderColor: mw.color.accent, boxShadow: `0 0 0 3px ${mw.color.accentSoft}` },
            }}>
              <SearchIcon sx={{ color: mw.color.textFaint, fontSize: 19 }} />
              <InputBase placeholder={t('Search menus...')} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} sx={{ fontSize: 14.5, flex: 1, color: mw.color.textPrimary }} />
            </Box>
          </Box>

          {/* Table Header */}
          <Box sx={{ overflowX: 'auto' }}>
          <Box sx={{ minWidth: 760, display: 'grid', gridTemplateColumns: MENU_GRID, columnGap: 2, px: 3, py: 1.5, bgcolor: mw.color.headBg, borderTop: `1px solid ${mw.color.border}`, borderBottom: `1px solid ${mw.color.border}` }}>
            {[t('Name'), t('Menu Hours'), t('Categories'), t('Items'), ''].map((h, i) => (
              <Typography key={i} sx={{ fontSize: 11, fontWeight: 700, color: mw.color.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{h}</Typography>
            ))}
          </Box>

          {queryLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
          ) : filteredMenus.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <MenuBookIcon sx={{ fontSize: 44, color: mw.color.borderStrong, mb: 1 }} />
              <Typography sx={{ color: mw.color.textMuted }}>
                {menus.length === 0 ? t('No menus yet. Click "+ New Menu" to create one.') : t('No menus match your search.')}
              </Typography>
            </Box>
          ) : filteredMenus.map((menu, i) => {
            const categories = getCategories(menu)
            const itemCount = menu.items?.length || 0
            return (
              <Box key={menu._id} sx={{ minWidth: 760, minHeight: 68, display: 'grid', gridTemplateColumns: MENU_GRID, columnGap: 2, px: 3, py: 1.75, alignItems: 'center', borderBottom: i < filteredMenus.length - 1 ? `1px solid ${mw.color.divider}` : 'none', transition: 'background-color .12s ease', '&:hover': { bgcolor: mw.color.hoverBg }, cursor: 'pointer' }}
                onClick={() => handleOpenEdit(menu)}>
                {/* Name */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                  {/* The Catering Menu (migration 134) the server keeps: every
                      catering category is on it. */}
                  <Box sx={{ width: 40, height: 40, borderRadius: mw.radius.sm, bgcolor: menu.isCatering ? CATERING.soft : mw.color.aiSoft, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {menu.isCatering
                      ? <RoomServiceRoundedIcon sx={{ fontSize: 20, color: CATERING.accent }} />
                      : <MenuBookIcon sx={{ fontSize: 19, color: mw.color.ai }} />}
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography sx={{ fontWeight: 700, fontSize: 15, color: mw.color.textPrimary }}>{menu.name}</Typography>
                      {menu.isCatering && <CateringBadge />}
                    </Box>
                    {menu.isCatering && (
                      <Typography sx={{ fontSize: 12.5, color: mw.color.textMuted, mt: 0.25 }}>
                        {t('Holds your catering categories automatically')}
                      </Typography>
                    )}
                  </Box>
                </Box>

                {/* Hours — a bordered pill */}
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, justifySelf: 'start', maxWidth: '100%', minHeight: 30, px: 1.25, py: 0.5, borderRadius: '15px', border: `1px solid ${mw.color.border}`, bgcolor: mw.color.cardBg }}>
                  <AccessTimeIcon fontSize="small" sx={{ color: mw.color.textMuted, fontSize: 16, flexShrink: 0 }} />
                  <Typography sx={{ fontSize: 13, fontWeight: 500, color: mw.color.textSecondary, fontVariantNumeric: 'tabular-nums', lineHeight: 1.35 }}>{formatMenuHours(menu)}</Typography>
                </Box>

                {/* Categories */}
                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                  {categories.length === 0 ? (
                    <Typography sx={{ fontSize: 14, color: mw.color.textFaint }}>—</Typography>
                  ) : (
                    <>
                      {categories.slice(0, 3).map(cat => (
                        <Chip key={cat} label={cat} size="small" sx={chipSx.category} />
                      ))}
                      {categories.length > 3 && (
                        <Chip label={`+${categories.length - 3}`} size="small" sx={chipSx.overflow} />
                      )}
                    </>
                  )}
                </Box>

                {/* Items count */}
                <Typography sx={{ fontSize: 14.5, color: mw.color.textMuted }}><Box component="span" sx={{ ...numSx, color: mw.color.textPrimary }}>{itemCount}</Box> {t('items')}</Typography>

                {/* Actions */}
                <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }} onClick={e => e.stopPropagation()}>
                  {onPreview && (
                    <Tooltip title={t('Preview')}>
                      <IconButton size="small" onClick={() => onPreview(menu._id)} sx={iconBtnSx}>
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title={t('Edit')}>
                    <IconButton size="small" onClick={() => handleOpenEdit(menu)} sx={iconBtnSx}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={menu.isCatering ? t('Your Catering Menu stays while you have catering categories. Turn off Catering on them to remove it.') : t('Delete')}>
                    <span>
                      <IconButton size="small" disabled={!!menu.isCatering} onClick={() => handleDelete(menu._id)} sx={iconBtnDangerSx}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </Box>
            )
          })}
          </Box>
        </Paper>
      </Box>

      {/* AI Upload Dialog */}
      <Dialog open={aiOpen} onClose={() => setAiOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: dialogPaperSx }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 3, px: 3.5, pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Box sx={{ width: 38, height: 38, borderRadius: mw.radius.sm, bgcolor: mw.color.aiSoft, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <AutoAwesomeIcon sx={{ color: mw.color.ai, fontSize: 21 }} />
            </Box>
            <Typography sx={displaySx(24)}>{t('AI Menu Upload')}</Typography>
          </Box>
          <IconButton size="small" onClick={() => setAiOpen(false)} sx={iconBtnSx}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3.5, pb: 3 }}>
          <Typography variant="body2" sx={{ color: mw.color.textMuted, mb: 2 }}>
            {t('Upload a photo or PDF of your menu. Our AI will scan it and automatically create items for you.')}
          </Typography>
          <AIFileUpload restaurantId={restaurantId} onParseComplete={() => {
            refetchMenusList()
            if (onUploadComplete) onUploadComplete()
            setTimeout(() => setAiOpen(false), 3000)
          }} />
        </DialogContent>
      </Dialog>

      {/* AI Upload INTO the currently-edited menu */}
      <Dialog open={aiIntoMenuOpen} onClose={() => setAiIntoMenuOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: dialogPaperSx }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 3, px: 3.5, pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Box sx={{ width: 38, height: 38, borderRadius: mw.radius.sm, bgcolor: mw.color.aiSoft, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <AutoAwesomeIcon sx={{ color: mw.color.ai, fontSize: 21 }} />
            </Box>
            <Typography sx={displaySx(24)}>{t('AI Menu Upload')}</Typography>
          </Box>
          <IconButton size="small" onClick={() => setAiIntoMenuOpen(false)} sx={iconBtnSx}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3.5, pb: 3 }}>
          <Typography variant="body2" sx={{ color: mw.color.textMuted, mb: 2 }}>
            {t('Upload a photo or PDF. Missing items are created and added to this menu; items you already have are added too.')}
          </Typography>
          {editingMenu && (
            <AIFileUpload
              restaurantId={restaurantId}
              existingMenuId={editingMenu._id}
              existingMenuName={editingMenu.name}
              onParseComplete={() => {
                refetchMenuItems()
                refetchMenusList()
                if (onUploadComplete) onUploadComplete()
                setTimeout(() => setAiIntoMenuOpen(false), 3000)
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth={editingMenu ? 'md' : 'sm'} fullWidth PaperProps={{ sx: { ...dialogPaperSx, maxHeight: '90vh' } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 3, px: 3.5, pb: 1.5, borderBottom: `1px solid ${mw.color.divider}` }}>
          <Typography sx={displaySx(26)}>
            {editingMenu ? t('Edit Menu') : t('New Menu')}
          </Typography>
          <IconButton size="small" onClick={handleCloseDialog} sx={iconBtnSx}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3.5, pb: 1 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <TextField autoFocus fullWidth label={t('Menu Name')} value={name} onChange={e => setName(e.target.value)} margin="normal" required placeholder={t('e.g., Lunch Menu, Breakfast Special')} />
          <TextField fullWidth label={t('Description')} value={description} onChange={e => setDescription(e.target.value)} margin="normal" multiline rows={2} placeholder={t('Optional description')} />
          {/* Hours. One window for the whole week, or a window per day for a
              menu that keeps different times (or does not run) on some days. */}
          <TextField
            select
            fullWidth
            margin="normal"
            label={t('Hours')}
            value={hoursMode}
            onChange={e => {
              const next = e.target.value
              // Carry the single window onto every day so switching modes starts
              // from what the menu already served, not blank fields.
              const hasAnyTime = MENU_DAYS.some(d => (daySchedule[d]?.windows || []).some(w => w.start || w.end))
              if (next === 'BY_DAY' && !hasAnyTime) {
                setDaySchedule(emptyDaySchedule(startTime, endTime))
              }
              setHoursMode(next)
            }}
          >
            <MenuItem value="EVERY_DAY">{t('Same hours every day')}</MenuItem>
            <MenuItem value="BY_DAY">{t('Different hours by day')}</MenuItem>
          </TextField>

          {hoursMode === 'EVERY_DAY' ? (
            <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
              <TextField fullWidth type="time" label={t('Start Time')} value={startTime} onChange={e => setStartTime(e.target.value)} InputLabelProps={{ shrink: true }} helperText={t('Leave both blank for all day.')} />
              <TextField fullWidth type="time" label={t('End Time')} value={endTime} onChange={e => setEndTime(e.target.value)} InputLabelProps={{ shrink: true }} />
            </Box>
          ) : (
            <Box sx={{ mt: 1, border: `1px solid ${mw.color.border}`, borderRadius: mw.radius.panel, p: 1.5, bgcolor: mw.color.headBg }}>
              {MENU_DAYS.map(day => {
                const entry = daySchedule[day] || { open: false, windows: [{ start: '', end: '' }] }
                const windows = entry.windows && entry.windows.length ? entry.windows : [{ start: '', end: '' }]
                const setDay = (patch) => setDaySchedule(prev => ({ ...prev, [day]: { ...prev[day], ...patch } }))
                const setWindow = (i, patch) => setDay({
                  windows: windows.map((w, idx) => (idx === i ? { ...w, ...patch } : w)),
                })
                return (
                  <Box key={day} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 0.75 }}>
                    <Switch size="small" checked={entry.open} onChange={e => setDay({ open: e.target.checked })} />
                    <Typography variant="body2" sx={{ width: 88, mt: 1, color: entry.open ? mw.color.textPrimary : mw.color.textFaint, fontWeight: 600 }}>
                      {t(DAY_LABEL[day])}
                    </Typography>
                    {entry.open ? (
                      <Box sx={{ flex: 1 }}>
                        {windows.map((w, i) => (
                          <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <TextField
                              size="small" type="time" value={w.start || ''}
                              onChange={e => setWindow(i, { start: e.target.value })}
                              InputLabelProps={{ shrink: true }} sx={{ width: 130 }}
                            />
                            <Typography variant="body2" sx={{ color: mw.color.textFaint }}>{t('to')}</Typography>
                            <TextField
                              size="small" type="time" value={w.end || ''}
                              onChange={e => setWindow(i, { end: e.target.value })}
                              InputLabelProps={{ shrink: true }} sx={{ width: 130 }}
                            />
                            {windows.length > 1 && (
                              <Tooltip title={t('Remove this window')}>
                                <IconButton
                                  size="small"
                                  onClick={() => setDay({ windows: windows.filter((_, idx) => idx !== i) })}
                                >
                                  <RemoveCircleOutlineIcon fontSize="small" sx={{ color: mw.color.textFaint }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        ))}
                        {windows.length < MAX_WINDOWS_PER_DAY && (
                          <Button
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={() => setDay({ windows: [...windows, { start: '', end: '' }] })}
                            sx={{ textTransform: 'none', color: mw.color.accent, fontWeight: 600 }}
                          >
                            {t('Add window')}
                          </Button>
                        )}
                      </Box>
                    ) : (
                      <Typography variant="body2" sx={{ mt: 1, color: mw.color.textFaint }}>{t('Closed')}</Typography>
                    )}
                  </Box>
                )
              })}
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <Button
                  size="small"
                  onClick={() => {
                    const mon = daySchedule.MON || { open: true, windows: [{ start: '', end: '' }] }
                    // Deep-copy the windows so each day owns its own list.
                    setDaySchedule(MENU_DAYS.reduce((acc, d) => ({
                      ...acc,
                      [d]: { open: mon.open, windows: (mon.windows || []).map(w => ({ ...w })) },
                    }), {}))
                  }}
                  sx={{ textTransform: 'none' }}
                >
                  {t('Copy Monday to all days')}
                </Button>
                <Button
                  size="small"
                  onClick={() => setDaySchedule(prev => ({
                    ...prev,
                    SAT: { ...prev.SAT, open: false },
                    SUN: { ...prev.SUN, open: false },
                  }))}
                  sx={{ textTransform: 'none' }}
                >
                  {t('Close weekends')}
                </Button>
              </Box>
              <Typography variant="caption" sx={{ color: mw.color.textMuted, display: 'block', mt: 1 }}>
                {t('Leave a day\'s times blank to serve it all day. Times are the restaurant\'s local time.')}
              </Typography>
            </Box>
          )}

          <TextField
            fullWidth
            type="number"
            label={t('Price Markup %')}
            value={markupPercent}
            onChange={e => setMarkupPercent(e.target.value)}
            margin="normal"
            placeholder="0"
            inputProps={{ min: 0, step: 0.5 }}
            InputProps={{ endAdornment: <Typography sx={{ color: mw.color.textMuted, fontWeight: 600 }}>%</Typography> }}
            helperText={t('Display-only markup added on top of EVERY price on this menu (items, sizes, add-ons). Base prices are never changed. 0 = no markup. Each price is rounded by the rule below.')}
          />

          {/* Rounding rule. Applied per price (item, each size, each add-on) after
              the markup, so the customer only ever sees final prices. */}
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <TextField
              select
              fullWidth
              label={t('Rounding')}
              value={roundingMode}
              onChange={e => setRoundingMode(e.target.value)}
              helperText={t('How marked-up prices are rounded.')}
            >
              {ROUNDING_OPTIONS.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>{t(opt.label)}</MenuItem>
              ))}
            </TextField>
            {roundingMode !== 'AS_IS' && (
              <TextField
                fullWidth
                type="number"
                label={t((ROUNDING_OPTIONS.find(o => o.value === roundingMode) || {}).valueLabel || 'Value')}
                value={roundingValue}
                onChange={e => setRoundingValue(e.target.value)}
                placeholder={roundingMode === 'UP_ENDING' || roundingMode === 'DOWN_ENDING' ? '0.99' : '0.25'}
                inputProps={{ min: 0, step: 0.05 }}
                InputProps={{ startAdornment: <Typography sx={{ color: mw.color.textMuted, fontWeight: 600, mr: 0.5 }}>$</Typography> }}
                helperText={t(describeRule(roundingMode, roundingValue))}
              />
            )}
          </Box>

          {/* Live preview so the owner sees the effect of markup + rounding. Shows
              the intermediate marked-up value so the rounding step is obvious
              (e.g. "round up to increment 0.99" looks very different from
              "round up to price ending 0.99"). */}
          {(() => {
            const pct = markupPercent === '' ? 0 : parseFloat(markupPercent)
            const val = roundingValue === '' ? 0 : parseFloat(roundingValue)
            const samples = [10.00, 9.50, 17.00]
            const fmt = (n) => `$${(Number(n) || 0).toFixed(2)}`
            // Validity is mode-aware: the ENDING modes accept 0 (a .00 ending =
            // whole-dollar prices), while increment/nearest modes need a positive
            // value (you can't round to a 0 increment). Without this the preview
            // vanished whenever the owner picked "round to ending .00".
            const isEnding = roundingMode === 'UP_ENDING' || roundingMode === 'DOWN_ENDING'
            const valueOk = roundingValue !== '' && !isNaN(val) && (isEnding ? val >= 0 : val > 0)
            const valid = !isNaN(pct) && (roundingMode === 'AS_IS' || valueOk)
            if (!valid) return null
            const hasMarkup = pct > 0
            return (
              <Box sx={{ mt: 1.5, p: 1.75, bgcolor: mw.color.headBg, border: `1px solid ${mw.color.border}`, borderRadius: mw.radius.panel }}>
                <Typography variant="caption" sx={{ color: mw.color.textMuted, fontWeight: 700, display: 'block', mb: 0.5 }}>
                  {t('Preview')} — {t('base price')}{hasMarkup ? ` → +${pct}% → ` : ' → '}{t('what the customer sees')}
                </Typography>
                {samples.map(s => {
                  const marked = hasMarkup ? s * (1 + pct / 100) : s
                  const final = roundComponentForMenu(s, pct, roundingMode, val)
                  return (
                    <Typography key={s} variant="body2" sx={{ color: mw.color.textSecondary, fontVariantNumeric: 'tabular-nums' }}>
                      {fmt(s)}
                      {hasMarkup && <span style={{ color: mw.color.textFaint }}> → {fmt(marked)}</span>}
                      {' → '}<strong>{fmt(final)}</strong>
                    </Typography>
                  )
                })}
                {roundingMode !== 'AS_IS' && (
                  <Typography variant="caption" sx={{ color: mw.color.textMuted, display: 'block', mt: 0.75 }}>
                    {t(describeRule(roundingMode, roundingValue))}
                  </Typography>
                )}
              </Box>
            )
          })()}

          {/* Per-category / per-item overrides (only when editing — needs items).
              Precedence is item > category > menu; backend resolves it. */}
          {editingMenu && menuItems.length > 0 && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant="subtitle1" fontWeight="bold" sx={{ color: mw.color.textPrimary, mb: 0.5 }}>
                {t('Price overrides')}
              </Typography>
              <Typography variant="caption" sx={{ color: mw.color.textMuted, display: 'block', mb: 1 }}>
                {t('Optional. A category or item override beats the menu default above (item wins over category).')}
              </Typography>
              <MenuOverridesEditor
                categories={[...new Set(menuItems.map(i => i.categoryName).filter(Boolean))].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))}
                items={menuItems}
                menuDefault={{ pct: markupPercent === '' ? 0 : parseFloat(markupPercent) || 0, mode: roundingMode, value: roundingValue === '' ? 0 : parseFloat(roundingValue) || 0 }}
                categoryOverrides={categoryOverrides}
                setCategoryOverrides={setCategoryOverrides}
                itemOverrides={itemOverrides}
                setItemOverrides={setItemOverrides}
              />
            </>
          )}

          {editingMenu && (
            <>
              <Divider sx={{ my: 3 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="h6" fontWeight="bold" sx={{ color: mw.color.textPrimary, fontSize: 17, display: 'flex', alignItems: 'center' }}>
                  {t('Items in this Menu')}
                  <Chip label={menuItems.length} size="small" sx={{ ...chipSx.category, ...numSx, ml: 1, height: 24 }} />
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AutoAwesomeIcon fontSize="small" />}
                  onClick={() => setAiIntoMenuOpen(true)}
                  sx={{ ...aiBtnSx, height: 36, fontSize: 13.5 }}
                >
                  {t('AI Menu Upload')}
                </Button>
              </Box>
              {menuItems.length === 0 ? (
                <Typography variant="body2" sx={{ color: mw.color.textMuted, mb: 2 }}>{t('No items yet. Add items below.')}</Typography>
              ) : (
                <Paper variant="outlined" sx={{ maxHeight: 200, overflow: 'auto', mb: 2, borderRadius: mw.radius.panel, borderColor: mw.color.border }}>
                  <List dense disablePadding>
                    {menuItems.map(item => (
                      <ListItem key={item._id} sx={{ borderBottom: `1px solid ${mw.color.divider}`, '&:last-child': { borderBottom: 0 } }}>
                        <ListItemText
                          primary={<Typography variant="body2" fontWeight={600} sx={{ color: mw.color.textPrimary }}>{item.name}</Typography>}
                          secondary={
                            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.3, alignItems: 'center', flexWrap: 'wrap' }}>
                              {item.categoryName && <Chip label={item.categoryName} size="small" sx={{ ...chipSx.category, fontSize: 11.5, height: 22 }} />}
                              {item.basePrice != null && (() => {
                                const hasMarkup = item.markedUpPrice != null && item.markedUpPrice !== item.basePrice
                                return (
                                  <>
                                    <Chip
                                      label={`${t('Base')} $${Number(item.basePrice).toFixed(2)}`}
                                      size="small"
                                      sx={{ ...chipSx.overflow, borderStyle: 'solid', fontSize: 11.5, height: 22, fontVariantNumeric: 'tabular-nums' }}
                                    />
                                    {hasMarkup && (
                                      <Chip
                                        label={`$${Number(item.markedUpPrice).toFixed(2)}`}
                                        size="small"
                                        sx={{ ...chipSx.modifier, fontSize: 11.5, height: 22, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}
                                      />
                                    )}
                                  </>
                                )
                              })()}
                            </Box>
                          }
                        />
                        <ListItemSecondaryAction>
                          <Tooltip title={t('Remove from menu')}>
                            <IconButton edge="end" size="small" onClick={() => handleRemoveItem(item._id)} sx={iconBtnDangerSx}>
                              <RemoveCircleOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              )}

              <Typography variant="subtitle2" fontWeight="bold" sx={{ color: mw.color.textPrimary, mb: 1, mt: 2 }}>{t('Add Items')}</Typography>
              <TextField fullWidth size="small" placeholder={t('Search items...')} value={itemSearchQuery} onChange={e => setItemSearchQuery(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: mw.color.textFaint, fontSize: 19 }} /></InputAdornment> }}
                sx={{ mb: 1 }} />

              {filteredAvailableItems.length === 0 ? (
                <Typography variant="body2" sx={{ color: mw.color.textMuted, py: 1 }}>
                  {availableItems.length === 0 ? t('All items are already in this menu.') : t('No items match your search.')}
                </Typography>
              ) : (
                <Paper variant="outlined" sx={{ maxHeight: 180, overflow: 'auto', mb: 2, borderRadius: mw.radius.panel, borderColor: mw.color.border }}>
                  <List dense disablePadding>
                    {filteredAvailableItems.map(item => (
                      <ListItem key={item._id} sx={{ borderBottom: `1px solid ${mw.color.divider}`, '&:last-child': { borderBottom: 0 }, cursor: 'pointer', '&:hover': { bgcolor: mw.color.hoverBg } }} onClick={() => handleAddItem(item._id)}>
                        <ListItemText
                          primary={<Typography variant="body2" sx={{ color: mw.color.textPrimary }}>{item.name}{item.basePrice != null && <Typography component="span" variant="body2" sx={{ color: mw.color.textFaint, ml: 1, fontVariantNumeric: 'tabular-nums' }}>${item.basePrice}</Typography>}</Typography>}
                          secondary={item.categoryName ? <Typography variant="caption" sx={{ color: mw.color.textMuted }}>{item.categoryName}</Typography> : null}
                        />
                        <ListItemSecondaryAction>
                          <IconButton edge="end" size="small" onClick={e => { e.stopPropagation(); handleAddItem(item._id) }} sx={{ ...iconBtnSx, color: mw.color.accent, '&:hover': { bgcolor: mw.color.accentSoft, color: mw.color.accent } }}>
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              )}

              {allCategories.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ color: mw.color.textPrimary, mb: 1 }}>{t('Add by Category')}</Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {allCategories.map(cat => {
                      const count = availableItems.filter(i => i.categoryName && i.categoryName.toLowerCase().includes(cat.toLowerCase())).length
                      return (
                        <Chip key={cat} icon={<PlaylistAddIcon />} label={`${cat} (${count})`} size="small"
                          onClick={() => count > 0 && handleAddAllFromCategory(cat)} disabled={count === 0}
                          sx={{ height: 30, borderRadius: mw.radius.pill, cursor: count > 0 ? 'pointer' : 'default', bgcolor: count > 0 ? mw.color.cardBg : mw.color.subtleBg, color: count > 0 ? mw.color.textSecondary : mw.color.textFaint, border: `1px solid ${count > 0 ? mw.color.border : mw.color.divider}`, '& .MuiChip-icon': { color: count > 0 ? mw.color.accent : mw.color.textFaint, fontSize: 18 }, '&:hover': count > 0 ? { bgcolor: mw.color.accentSoft, borderColor: mw.color.accentBorder, color: mw.color.accentDark } : {}, fontWeight: 600 }} />
                      )
                    })}
                  </Box>
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={handleCloseDialog} disabled={saving} sx={pillBtnSx}>{t('Cancel')}</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={saving}
            sx={{ ...pillBtnPrimarySx, minWidth: 96 }}>
            {saving ? <CircularProgress size={20} /> : editingMenu ? t('Save') : t('Create')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default MenusTab
